import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { CommandPalette, useCommandPaletteShortcut, useRecentArticles } from '@ergeon/help-components';

import { authService } from 'common/auth';
import { defaultCategories } from 'data/help-categories';

import { WIKI_API_HOST, WIKI_DOMAIN, WIKI_PALETTE_LABELS } from './constants';

export type RecentArticle = { nodeKey: string; section?: string; title: string };

type WikiPaletteContextValue = {
  openPalette: () => void;
  recentArticles: RecentArticle[];
  rememberArticle: (article: RecentArticle) => void;
};

const noop = () => undefined;

const WikiPaletteContext = createContext<WikiPaletteContextValue>({
  openPalette: noop,
  recentArticles: [],
  rememberArticle: noop,
});

export const useWikiPalette = () => useContext(WikiPaletteContext);

const WikiPaletteProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const openPalette = useCallback(() => setIsOpen(true), []);
  const closePalette = useCallback(() => setIsOpen(false), []);
  const navigate = useNavigate();
  const navigateTo = useCallback((path: string) => navigate(path), [navigate]);
  const { recentArticles, rememberArticle } = useRecentArticles(WIKI_DOMAIN);

  useCommandPaletteShortcut(openPalette);

  const value = useMemo(
    () => ({ openPalette, recentArticles, rememberArticle }),
    [openPalette, recentArticles, rememberArticle],
  );

  return (
    <WikiPaletteContext.Provider value={value}>
      {children}
      <CommandPalette
        apiHost={WIKI_API_HOST}
        auth={authService}
        domain={WIKI_DOMAIN}
        isOpen={isOpen}
        labels={WIKI_PALETTE_LABELS}
        onClose={closePalette}
        onNavigate={navigateTo}
        quickLinks={defaultCategories}
        recentArticles={recentArticles} />
    </WikiPaletteContext.Provider>
  );
};

export default WikiPaletteProvider;
